import { useEffect, useState } from "react";
import { fetchSuccessionDetail } from "../lib/api";
import EmployeeDetailModal from "./EmployeeDetailModal";
import { Loader2, AlertTriangle, CheckCircle2, Clock, XCircle, Users, Shield } from "lucide-react";

const READINESS_ICON = {
  ready_now: <CheckCircle2 className="w-3.5 h-3.5" />,
  ready_1yr: <Clock className="w-3.5 h-3.5" />,
  ready_2_3yr: <Clock className="w-3.5 h-3.5" />,
  not_ready: <XCircle className="w-3.5 h-3.5" />,
};

export default function SuccessionDetailPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRole, setExpandedRole] = useState(null);
  const [detailEmpId, setDetailEmpId] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchSuccessionDetail()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-8 flex items-center justify-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Yedekleme planları yükleniyor...
      </div>
    );
  }
  if (!data) return null;

  const summary = data.summary || {};

  return (
    <div className="space-y-5" data-testid="succession-detail-panel">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-surface p-4 border-l-4 border-slate-500">
          <div className="label-eyebrow flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-slate-600" /> Kritik Rol
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{summary.total_critical_roles || 0}</div>
        </div>
        <div className="card-surface p-4 border-l-4 border-emerald-500">
          <div className="label-eyebrow flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Ready Now Var
          </div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{summary.roles_with_ready_now || 0}</div>
        </div>
        <div className="card-surface p-4 border-l-4 border-amber-500">
          <div className="label-eyebrow flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Yedeksiz
          </div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{summary.roles_without_successor || 0}</div>
        </div>
        <div className="card-surface p-4 border-l-4 border-red-500">
          <div className="label-eyebrow flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> Yüksek Risk
          </div>
          <div className="text-2xl font-bold text-red-600 mt-1">{summary.high_risk_roles || 0}</div>
        </div>
      </div>

      {/* Yedeksiz kritik roller */}
      {data.gaps?.length > 0 && (
        <div className="card-surface p-5 border-l-4 border-red-500 bg-red-50/30" data-testid="succession-gaps-panel">
          <div className="label-eyebrow text-red-700 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Yedek Aday Eksik Olan Kritik Roller
          </div>
          <div className="text-xs text-slate-600 mb-3">
            Aşağıdaki rollerde Ready Now veya 1 yıl içinde hazır aday yok — acil aksiyon gerektirir.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.gaps.map((g) => (
              <div key={g.role_id} className="flex items-start gap-2 p-3 rounded-lg bg-white border border-red-100">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-800">{g.role_title}</div>
                  <div className="text-xs text-slate-500">{g.department}</div>
                  <div className="text-[11px] text-red-700 mt-1">{g.reason} · {g.candidates_count} aday</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed critical roles list */}
      <div className="card-surface p-5">
        <div className="label-eyebrow mb-3 flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-sky-600" /> Kritik Rol Yedekleme Planları
        </div>
        <div className="space-y-3">
          {(data.roles || []).map((role) => {
            const isOpen = expandedRole === role.role_id;
            const buckets = role.readiness_buckets || {};
            return (
              <div key={role.role_id} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`succession-role-${role.role_id}`}>
                <button
                  onClick={() => setExpandedRole(isOpen ? null : role.role_id)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{role.role_title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{role.career_band}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        role.risk_level === "high" ? "bg-red-50 text-red-700" :
                        role.risk_level === "medium" ? "bg-amber-50 text-amber-700" :
                        "bg-emerald-50 text-emerald-700"
                      }`}>
                        Risk: {role.risk_level}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{role.department} · {role.status_label}</div>
                  </div>
                  {/* Readiness distribution bar */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span title="Ready Now" className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-medium">
                      <CheckCircle2 className="w-3 h-3" /> {buckets.ready_now || 0}
                    </span>
                    <span title="1 Yıl" className="flex items-center gap-1 px-2 py-0.5 rounded bg-sky-50 text-sky-700 text-[10px] font-medium">
                      <Clock className="w-3 h-3" /> {buckets.ready_1yr || 0}
                    </span>
                    <span title="2-3 Yıl" className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium">
                      <Clock className="w-3 h-3" /> {buckets.ready_2_3yr || 0}
                    </span>
                    {(buckets.not_ready || 0) > 0 && (
                      <span title="Hazır Değil" className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-medium">
                        <XCircle className="w-3 h-3" /> {buckets.not_ready}
                      </span>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-3">
                    {role.incumbents?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Mevcut</div>
                        <div className="flex gap-3 flex-wrap">
                          {role.incumbents.map((inc) => (
                            <div key={inc.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-100">
                              <img src={inc.avatar} alt={inc.name} className="w-8 h-8 rounded-full object-cover" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                              <div>
                                <div className="text-sm font-medium text-slate-800">{inc.name}</div>
                                <div className="text-[10px] text-slate-500">{inc.title}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Yedek Adaylar</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(role.candidates || []).map((c) => {
                          const rl = c.readiness_label || {};
                          return (
                            <button
                              type="button"
                              key={c.employee_id}
                              onClick={() => setDetailEmpId(c.employee_id)}
                              data-testid={`succession-candidate-${c.employee_id}`}
                              className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-slate-100 hover:border-indigo-300 hover:shadow-sm text-left transition-all"
                            >
                              <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full object-cover" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                                <div className="text-xs text-slate-500 truncate">{c.title}</div>
                                <div className="mt-1 flex items-center gap-2">
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                                    style={{ background: (rl.color || "#64748b") + "20", color: rl.color || "#64748b" }}
                                  >
                                    {READINESS_ICON[rl.code] || null}
                                    {rl.label || "–"}
                                  </span>
                                  <span className="text-[10px] text-slate-400">%{c.readiness_score} · {c.time_to_ready_months}ay</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        {(role.candidates || []).length === 0 && (
                          <div className="text-xs text-red-600 italic">Yedek aday bulunmuyor.</div>
                        )}
                      </div>
                    </div>
                    {role.rationale && (
                      <div className="text-xs text-slate-600 border-l-2 border-slate-300 pl-3 italic">{role.rationale}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Detail modal */}
      <EmployeeDetailModal
        employeeId={detailEmpId}
        open={!!detailEmpId}
        onOpenChange={(v) => !v && setDetailEmpId(null)}
      />
    </div>
  );
}
